import React, { useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  TextField,
  Button
} from '@material-ui/core';
import {
  cellRadiationSurveyValueUpdate,
  cellRadiationSurveyValueReset,
  saveCellRadiationSurvey,
  deleteCellRadiationSurvey,
  setCreateCellRadiationSurveyOpen,
  findCell,
  setConfirmDeleteCellRadiationSurveyOpen,
  findEntityPhotos
} from 'actions';
import { ConfirmDelete } from 'components';
import { ENTITY_TYPES } from 'common/constants';
import dateUtils from 'utils/dateUtils';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 750,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  container: {
    marginTop: theme.spacing(3)
  }
}));

const CreateCellRadiationSurvey = props => {
  const { cell, venues, customers, className, ...rest } = props;
  const dispatch = useDispatch();
  const classes = useStyles();

  const {
    saved,
    deleted,
    values,
    validationMessages,
    deleteMessage,
    openConfirmDelete,
    loading,
    open
  } = useSelector(state => state.cellRadiationSurveyCreate);

  const handleClose = () => {
    dispatch(cellRadiationSurveyValueReset());
    dispatch(setCreateCellRadiationSurveyOpen(false));
  };

  const handleFieldChange = (field, value) => {
    dispatch(
      cellRadiationSurveyValueUpdate(values => ({
        ...values,
        [field]: value
      }))
    );
  };

  const handleDateChange = (field, date) => {
    dispatch(
      cellRadiationSurveyValueUpdate(values => ({
        ...values,
        [field]: dateUtils.getUTCEpoch(date)
      }))
    );
  };

  useEffect(() => {
    if (saved || deleted) {
      dispatch(findCell(cell.id));
      dispatch(findEntityPhotos(ENTITY_TYPES.CELL, cell.id));
      dispatch(cellRadiationSurveyValueReset());
      dispatch(setCreateCellRadiationSurveyOpen(false));
    }
  }, [saved, deleted]);

  const handleCreateRadiationSurvey = () => {
    dispatch(saveCellRadiationSurvey(values, cell));
  };

  const handleDeleteRadiationSurvey = () => {
    dispatch(deleteCellRadiationSurvey(cell, values.guid));
  };

  const handleConfirmDeleteRadiationSurvey = () => {
    dispatch(setConfirmDeleteCellRadiationSurveyOpen(true));
  };

  const handleConfirmDeleteClose = () => {
    dispatch(setConfirmDeleteCellRadiationSurveyOpen(false));
  };

  if (!open || loading) {
    return null;
  }

  const create = !values.guid;

  return (
    <>
      <Modal onClose={handleClose} open={open}>
        <Card {...rest} className={clsx(classes.root, className)}>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              {create ? 'New' : 'Edit'} Radiation Survey
            </Typography>

            {cell && (
              <Typography align="center" gutterBottom variant="h4">
                {cell.name}
              </Typography>
            )}

            <Grid container className={classes.container} spacing={2}>
              <Grid item xs={12} md={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    error={validationMessages.hasOwnProperty('surveyDate')}
                    helperText={validationMessages.surveyDate}
                    margin="dense"
                    className={classes.datePicker}
                    id="filter-start-date-picker-dialog"
                    label="Radiation Survey Date"
                    format="MM/dd/yyyy"
                    value={
                      values.surveyDate !== null
                        ? new Date(values.surveyDate)
                        : null
                    }
                    onChange={date => handleDateChange('surveyDate', date)}
                    KeyboardButtonProps={{
                      'aria-label': 'change date'
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={handleClose} variant="contained">
              Cancel
            </Button>
            {!create && (
              <Button
                className={classes.saveButton}
                onClick={handleConfirmDeleteRadiationSurvey}
                variant="contained">
                Delete
              </Button>
            )}
            <Button
              className={classes.saveButton}
              onClick={handleCreateRadiationSurvey}
              variant="contained">
              Save
            </Button>
          </CardActions>
        </Card>
      </Modal>

      <ConfirmDelete
        open={openConfirmDelete}
        message={deleteMessage}
        disabled={deleteMessage !== null}
        entityType="version"
        onClose={handleConfirmDeleteClose}
        onConfirm={handleDeleteRadiationSurvey}
      />
    </>
  );
};

CreateCellRadiationSurvey.defaultProps = {
  open: false
};

export default CreateCellRadiationSurvey;
